import { useCallback, useEffect, useState } from "react";

import { TrackedDriver } from "../types";
import { setIconPathByRotation } from "../utils/setIconPathByRotation";

type UseActiveDriverIconProps = {
  map: google.maps.Map;
  trackedDrivers: TrackedDriver[];
};

const activeAnimations = new Map<google.maps.Marker, number>();

const animateMarker = (
  marker: google.maps.Marker,
  from: google.maps.LatLngLiteral,
  to: google.maps.LatLngLiteral,
  duration: number
) => {
  const prevRafId = activeAnimations.get(marker);
  if (prevRafId != null) {
    cancelAnimationFrame(prevRafId);
  }

  const startTime = performance.now();
  const clampedDuration = Math.max(Math.min(duration, 2000), 16);

  const step = (now: number) => {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / clampedDuration, 1);

    marker.setPosition({
      lat: from.lat + (to.lat - from.lat) * t,
      lng: from.lng + (to.lng - from.lng) * t,
    });

    if (t < 1) {
      const rafId = requestAnimationFrame(step);
      activeAnimations.set(marker, rafId);
    } else {
      activeAnimations.delete(marker);
    }
  };

  const rafId = requestAnimationFrame(step);
  activeAnimations.set(marker, rafId);
};

function useActiveDriverIcon(props: UseActiveDriverIconProps) {
  const { map, trackedDrivers } = props;

  // state
  const [driverMarkers, setDriverMarkers] = useState<
    Map<string, google.maps.Marker>
  >(new Map());

  // event handlers
  const setMapBound = useCallback(() => {
    if (trackedDrivers.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    trackedDrivers.forEach(
      ({
        location: {
          coords: { latitude: lat, longitude: lng },
        },
      }) => {
        if (lat && lng) {
          bounds.extend({ lat, lng });
        }
      }
    );

    if (map) {
      map.setCenter(bounds.getCenter());
      map.fitBounds(bounds);
      if (trackedDrivers.length === 1) {
        map.setZoom(11);
      }
    }
  }, [trackedDrivers, map]);

  // add, remove, and update drivers by comparing previous markers to incoming trackedDrivers
  useEffect(() => {
    let shouldResetMapBounds = false;
    let shouldUpdateDriverMarkers = false;
    const newDriverMarkers = new Map();
    trackedDrivers.forEach(
      ({
        id,
        dispatchStatus,
        location: {
          coords: { latitude: lat, longitude: lng, heading },
          timestamp: currentTimeStamp,
        },
      }) => {
        if (lat && lng) {
          // if trackedDriver id does not exist in previous markers, create new Marker
          if (!driverMarkers.has(id)) {
            shouldResetMapBounds = true;
            shouldUpdateDriverMarkers = true;
            const marker = new google.maps.Marker({
              position: { lat, lng },
              icon: createDriverIcon(heading, dispatchStatus),
              map: map,
              zIndex: 100,
            });
            marker.set("timestamp", currentTimeStamp);
            marker.set("dispatchStatus", dispatchStatus);
            newDriverMarkers.set(id, marker);
          } else {
            // if trackedDriver id exists in previous markers, and timestamp or dispatchStatus changed, update Marker
            const previousMarker = driverMarkers.get(id);
            if (
              currentTimeStamp !== previousMarker["timestamp"] ||
              dispatchStatus !== previousMarker["dispatchStatus"]
            ) {
              shouldUpdateDriverMarkers = true;

              const prevPos = previousMarker.getPosition();
              const from = prevPos
                ? { lat: prevPos.lat(), lng: prevPos.lng() }
                : { lat, lng };
              const timeSinceLastUpdate =
                currentTimeStamp - (previousMarker["timestamp"] as number);

              animateMarker(
                previousMarker,
                from,
                { lat, lng },
                timeSinceLastUpdate
              );
              previousMarker.setIcon(createDriverIcon(heading, dispatchStatus));
              previousMarker.set("timestamp", currentTimeStamp);
              previousMarker.set("dispatchStatus", dispatchStatus);
            }
            newDriverMarkers.set(id, previousMarker);
          }
        }
      }
    );

    // remove inactive drivers on map
    driverMarkers.forEach((prevMarker, prevDriverId) => {
      if (!newDriverMarkers.has(prevDriverId)) {
        shouldUpdateDriverMarkers = true;
        shouldResetMapBounds = true;
        prevMarker.setMap(null);
      }
    });

    if (shouldResetMapBounds) setMapBound();
    if (shouldUpdateDriverMarkers) setDriverMarkers(newDriverMarkers);

    return () => {
      activeAnimations.forEach((rafId) => cancelAnimationFrame(rafId));
      activeAnimations.clear();
    };
  }, [trackedDrivers, map, setMapBound, driverMarkers]);
}

const createDriverIcon = (rotation: number, dispatchStatus?: string) => {
  return {
    url: setIconPathByRotation(rotation, dispatchStatus),
    anchor: new google.maps.Point(12, 20),
    labelOrigin: new google.maps.Point(16, 42),
    scaledSize: new google.maps.Size(32, 32),
  };
};

export default useActiveDriverIcon;
